import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import swal from "sweetalert";
import { AuthContext } from "../../../context/AuthContext";
import { saveUddyamaanAssessment, getUddyamaanAssessmentResult } from "../../../services/adminApiService/uddyamaanApi/uddyamaanApi";
import ConfirmModal from "../editProfile/ConfirmModal";
import { scoreAllTests } from "./fitnessScoring";
import ProtsaahanAssessmentModal from "./ProtsaahanAssessmentModal";

const STAGE_LABELS = { 7: "Nyaya Panchayat", 6: "Block", 3: "District" };

const EMPTY = {
  shuttle_run_seconds: '', shuttle_run_points: '',
  flying_run_seconds: '', flying_run_points: '',
  run_600m_minutes: '', run_600m_seconds: '', run_600m_points: '',
  broad_jump_cm: '', broad_jump_points: '',
  medicine_ball_meters: '', medicine_ball_cm: '', medicine_ball_points: '',
  forward_bend_cm: '', forward_bend_points: '',
  measured_height_cm: '', measured_weight_kg: '',
  sitting_height_cm: '', arm_span_cm: '', leg_length_cm: '',
  bmi: '',
  notes: '',
};

const POINT_FIELDS = [
  'shuttle_run_points', 'flying_run_points', 'run_600m_points',
  'broad_jump_points', 'medicine_ball_points', 'forward_bend_points',
];

const UddyamaanAssessmentModal = ({ show, onHide, application }) => {
  const { userData } = useContext(AuthContext);
  const stageLabel = STAGE_LABELS[Number(userData?.user_level)];
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [existingResult, setExistingResult] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    setShowConfirm(false);
    if (!show || !application?.id) return;
    setFetching(true);
    getUddyamaanAssessmentResult(application.id)
      .then(res => {
        const body = res?.data;
        const d = body?.data;
        const hasResult = body?.success && d && typeof d === 'object' && !Array.isArray(d) && Object.keys(d).length > 0;
        if (hasResult) {
          setExistingResult(true);
          setForm({
            shuttle_run_seconds:  d.shuttle_run_seconds  ?? '',
            shuttle_run_points:   d.shuttle_run_points   ?? '',
            flying_run_seconds:   d.flying_run_seconds   ?? '',
            flying_run_points:    d.flying_run_points    ?? '',
            run_600m_minutes:     d.run_600m_minutes     ?? '',
            run_600m_seconds:     d.run_600m_seconds     ?? '',
            run_600m_points:      d.run_600m_points      ?? '',
            broad_jump_cm:        d.broad_jump_cm        ?? '',
            broad_jump_points:    d.broad_jump_points    ?? '',
            medicine_ball_meters: d.medicine_ball_meters ?? '',
            medicine_ball_cm:     d.medicine_ball_cm     ?? '',
            medicine_ball_points: d.medicine_ball_points ?? '',
            forward_bend_cm:      d.forward_bend_cm      ?? '',
            forward_bend_points:  d.forward_bend_points  ?? '',
            measured_height_cm:   d.measured_height_cm   ?? '',
            measured_weight_kg:   d.measured_weight_kg   ?? '',
            sitting_height_cm:    d.sitting_height_cm    ?? '',
            arm_span_cm:          d.arm_span_cm          ?? '',
            leg_length_cm:        d.leg_length_cm        ?? '',
            bmi:                  '',
            notes:                d.notes                ?? '',
          });
        } else {
          setExistingResult(false);
          setForm(EMPTY);
        }
      })
      .catch(() => { setExistingResult(false); setForm(EMPTY); })
      .finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, application?.id]);

  useEffect(() => {
    if (form.measured_height_cm && form.measured_weight_kg) {
      const m = form.measured_height_cm / 100;
      const computed = (form.measured_weight_kg / (m * m)).toFixed(1);
      setForm(f => (f.bmi === computed ? f : { ...f, bmi: computed }));
    } else if (form.bmi !== '') {
      setForm(f => ({ ...f, bmi: '' }));
    }
  }, [form.measured_height_cm, form.measured_weight_kg]);

  useEffect(() => {
    if (existingResult) return;
    const computed = scoreAllTests(application?.gender, application?.dob, form);
    setForm(f => {
      const changed = Object.keys(computed).some(k => f[k] !== computed[k]);
      return changed ? { ...f, ...computed } : f;
    });
  }, [
    existingResult, application?.gender, application?.dob,
    form.shuttle_run_seconds, form.flying_run_seconds,
    form.run_600m_minutes, form.run_600m_seconds,
    form.broad_jump_cm, form.medicine_ball_meters, form.medicine_ball_cm,
    form.forward_bend_cm,
  ]);

  const set = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleConfirmedSave = async () => {
    setSaving(true);
    try {
      await saveUddyamaanAssessment(application.id, form);
      swal("Saved", "Assessment results saved successfully.", "success");
      onHide(true);
    } catch (err) {
      swal("Error", err?.response?.data?.message || "Failed to save assessment.", "error");
    } finally {
      setSaving(false);
    }
  };

  const num = (name, extra = {}) => ({
    name, value: form[name], onChange: existingResult ? undefined : set,
    type: "number", min: "0", step: "0.01", className: "form-control",
    readOnly: existingResult,
    ...extra,
  });

  const pt = (name, extra = {}) => ({
    name, value: form[name],
    type: "number", min: "0", max: "10", step: "1", className: "form-control",
    readOnly: true, disabled: true,
    placeholder: "0",
    ...extra,
  });

  const totalPoints = POINT_FIELDS.reduce((sum, f) => sum + (Number(form[f]) || 0), 0);

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18, fontWeight: 600 }}>
          {existingResult ? 'View Assessment' : 'Conduct Assessment'}{stageLabel ? ` (${stageLabel})` : ''} — {application?.name || ""}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleFormSubmit}>
        <Modal.Body>
          {fetching ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <Row className="g-3">
              <Col xs={12}>
                <h6 className="fw-semibold mb-0" style={{ color: '#6366F1' }}>Anthropometric Measurements</h6>
              </Col>
              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Standing Height</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("measured_height_cm")} placeholder="0.00" />
                  <span className="input-group-text">cm</span>
                </div>
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Body Weight</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("measured_weight_kg")} placeholder="0.00" />
                  <span className="input-group-text">kg</span>
                </div>
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Sitting Height</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("sitting_height_cm")} placeholder="0.00" />
                  <span className="input-group-text">cm</span>
                </div>
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Arm Span</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("arm_span_cm")} placeholder="0.00" />
                  <span className="input-group-text">cm</span>
                </div>
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Leg Length</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("leg_length_cm")} placeholder="0.00" />
                  <span className="input-group-text">cm</span>
                </div>
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>BMI</Form.Label>
                <div className="input-group mb-2">
                  <input className="form-control" value={form.bmi} readOnly disabled placeholder="—" />
                  <span className="input-group-text">kg/m²</span>
                </div>
              </Col>

              <Col xs={12}>
                <h6 className="fw-semibold mb-0 mt-2" style={{ color: '#6366F1' }}>Fitness Tests</h6>
              </Col>
              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>6x10 Shuttle Run</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("shuttle_run_seconds")} placeholder="0.00" />
                  <span className="input-group-text">sec</span>
                </div>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-10)</Form.Label>
                <input {...pt("shuttle_run_points")} />
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>30m Flying Run</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("flying_run_seconds")} placeholder="0.00" />
                  <span className="input-group-text">sec</span>
                </div>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-10)</Form.Label>
                <input {...pt("flying_run_points")} />
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>600m Run</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <div className="input-group">
                    <input {...num("run_600m_minutes", { step: "1" })} placeholder="0" />
                    <span className="input-group-text">min</span>
                  </div>
                  <div className="input-group">
                    <input {...num("run_600m_seconds", { step: "1", max: "59" })} placeholder="0" />
                    <span className="input-group-text">sec</span>
                  </div>
                </div>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-10)</Form.Label>
                <input {...pt("run_600m_points")} />
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Standing Broad Jump</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("broad_jump_cm")} placeholder="0.00" />
                  <span className="input-group-text">cm</span>
                </div>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-10)</Form.Label>
                <input {...pt("broad_jump_points")} />
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Medicine Ball Throw (1 kg)</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <div className="input-group">
                    <input {...num("medicine_ball_meters", { step: "1" })} placeholder="0" />
                    <span className="input-group-text">m</span>
                  </div>
                  <div className="input-group">
                    <input {...num("medicine_ball_cm", { step: "1", max: "99" })} placeholder="0" />
                    <span className="input-group-text">cm</span>
                  </div>
                </div>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-10)</Form.Label>
                <input {...pt("medicine_ball_points")} />
              </Col>

              <Col md={6}>
                <Form.Label style={{ fontWeight: 500 }}>Forward Bend Reach</Form.Label>
                <div className="input-group mb-2">
                  <input {...num("forward_bend_cm")} placeholder="0.00" />
                  <span className="input-group-text">cm</span>
                </div>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>Points (0-10)</Form.Label>
                <input {...pt("forward_bend_points")} />
              </Col>

              <Col xs={12}>
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{ background: "#EEF2FF", borderRadius: 8, border: "1px solid #C7D2FE" }}
                >
                  <span style={{ fontWeight: 600, color: "#4338CA" }}>Total Points</span>
                  <span style={{ fontWeight: 700, fontSize: 20, color: "#4338CA" }}>{totalPoints} / 60</span>
                </div>
              </Col>

              <Col xs={12}>
                <Form.Label style={{ fontWeight: 500 }}>Notes (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={existingResult ? undefined : set}
                  readOnly={existingResult}
                  placeholder="Any additional observations..."
                />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex gap-2 w-100">
            <Button variant="outline-secondary" className="flex-fill" onClick={() => onHide(false)} disabled={saving}>
              {existingResult ? 'Close' : 'Cancel'}
            </Button>
            {!existingResult && (
              <Button
                type="submit"
                className="flex-fill"
                disabled={saving || fetching}
                style={{ backgroundColor: "#7C3AED", border: "none" }}
              >
                {saving ? "Saving..." : "Save Assessment"}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Form>
      <ConfirmModal
        type="save-assessment"
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmedSave}
      />
    </Modal>
  );
};

// Dispatches to the Protsaahan-specific flow (battery test -> skill test)
// for sport-based applications, otherwise renders the generic Uddyamaan
// fitness assessment unchanged. `sport` is a reliable proxy for "this is a
// Protsaahan application" (see UddyamaanApplicationService::apply, which
// force-nulls sport for every other scheme).
const AssessmentFormModal = ({ show, onHide, application }) => (
  application?.sport
    ? <ProtsaahanAssessmentModal show={show} onHide={onHide} application={application} />
    : <UddyamaanAssessmentModal show={show} onHide={onHide} application={application} />
);

export default AssessmentFormModal;
